import { useCallback, useState } from 'react';

import {
  useFragment as useApolloFragment,
  useQuery,
} from '@apollo/client/react';
import { Button, Skeleton, Stack } from '@mui/material';
import { ChevronRight } from 'lucide-react';

import type { GetPropertyAppraisableQuery } from '@graphql/__generated__/graphql';
import AlertPropertyAppraisable from '@modules/_shared/components/AlertPropertyAppraisable';
import { GET_PROPERTY_APPRAISABLE } from '@modules/_shared/components/appraisal/appraisalQueries';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { PropertyForm } from '@modules/_shared/components/property-form/PropertyForm';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { STEP_HEDONISTIC_VALUATION_FRAGMENT } from '@modules/reports/cma-reports/cmaReportsQueries';
import AppraisalSection from '@modules/reports/cma-reports/step-listing-appraise/AppraisalSection';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useUpdateCmaReport } from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';

export type AppraisalResult = NonNullable<
  GetPropertyAppraisableQuery['property_appraisable']
>['appraisable'];

const StepHedonisticValuation = (props: ReportComponentProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const { reportId: cmaReportId } = props;

  const { data, complete } = useApolloFragment({
    fragment: STEP_HEDONISTIC_VALUATION_FRAGMENT,
    fragmentName: 'StepHedonisticValuation',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const latestAppraisal = data?.property?.latest_appraisal;

  const { data: propertyAppraisable, loading: appraisalLoading } = useQuery(
    GET_PROPERTY_APPRAISABLE,
    {
      variables: {
        propertyId: data?.property?.id ?? '',
      },
      skip: data?.property?.id == null,
    },
  );
  const appraisable = propertyAppraisable?.property_appraisable?.appraisable;

  const [updateCmaReport] = useUpdateCmaReport(
    cmaReportId,
    'page-hedonistic-valuation',
  );

  const handleAppraiseProperty = useCallback(async () => {
    updateCmaReport({});
  }, [updateCmaReport]);

  return (
    <>
      <Drawer
        title={t('Edit property')}
        open={open}
        slotProps={{ paper: { sx: { maxWidth: 1200 } } }}
        onClose={() => setOpen(false)}
        sx={{ zIndex: 1300 }}
      >
        <PropertyForm
          propertyId={data?.property?.id}
          onCancel={() => setOpen(false)}
          onSave={() => {
            setOpen(false);
            updateCmaReport({});
          }}
        />
      </Drawer>
      {!complete ? (
        <Stack
          sx={{
            gap: 3,
            p: 2,
          }}
        >
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Skeleton variant="rounded" width={100} height={24} />
            <Skeleton variant="rounded" width={160} height={24} />
          </Stack>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Skeleton variant="rounded" height={24} width={150} />
            <Skeleton variant="rounded" height={24} width={140} />
            <Skeleton variant="rounded" height={36} width={200} />
          </Stack>
        </Stack>
      ) : (
        <Stack
          sx={{
            height: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            sx={{
              p: 2,
              gap: 2,
              height: '100%',
            }}
          >
            <Button
              fullWidth
              endIcon={<ChevronRight />}
              onClick={() => setOpen(true)}
              variant="contained"
            >
              {t('Edit property')}
            </Button>

            <AppraisalSection
              propertyId={data?.property?.id}
              latestAppraisalId={latestAppraisal?.id}
              appraisalsLength={data?.property?.appraisals?.length ?? 0}
              appraisalLoading={appraisalLoading}
              appraisable={appraisable}
              onAppraise={handleAppraiseProperty}
            />

            {appraisable?.result === false && (
              <AlertPropertyAppraisable errors={appraisable.errors} />
            )}
          </Stack>

          <FooterActionsComponentWithoutForm updating={false} {...props} />
        </Stack>
      )}
    </>
  );
};

export default StepHedonisticValuation;
